// AI managers (DESIGN.md §2.16): our own designed managers with a skill
// and personality dials. Not LLMs. They see exactly what a human sees (the
// rankings, their roster, the clock) and pick from it with their own bias.

import { BAND_SKILL, CAST, byBand } from '@fsp/bots';
import type { DraftContext, DraftState } from './engine';
import { atLeagueCap, bestAvailable, isTaken, needs, picksLeft, rosterOf } from './engine';
import {
  draftStyleOf,
  roomPull,
  styleLook,
  stylePull,
  styleRefuses,
  type DraftStyle,
} from './style';

// THE DIALS ARE THE BOT'S STYLE, and each one moves something (AI_MANAGERS.md
// §4.4): how often it looks at its team, how often it offers a trade, what it
// keeps, what it gambles on. How WELL it does any of that is `skill`, a
// separate axis — the goofy bot is not the bad bot. What each dial does between
// drafts is `packages/bots` (`styleOf`); in a draft, `activity` is think time,
// `risk` blurs a pick and says how wary it is of the injury report, and
// `aggression` lifts its bids at an auction. How it drafts beyond that is its
// draft style, in its file (`style.ts`, AI_MANAGERS.md §4.7).
export interface AiDials {
  /** 0 silent … 1 relentless. */
  trashTalk: number;
  /** 0 never offers a trade … 1 offers them often (and bids high for a player it wants). */
  aggression: number;
  /** 0 sits a Questionable starter for a healthy one … 1 starts him and stashes injured players. */
  risk: number;
  /** 0 churns … 1 keeps what it drafted. */
  loyalty: number;
  /** 0 changes only what it must … 1 looks at its team every day. Also think time in a draft. */
  activity: number;
}

export interface AiManager {
  presetId: string;
  name: string;
  /** One-line voice, shown on the seat. */
  voice: string;
  /** How well it plays, 0 to 100, in five bands (`@fsp/bots` `skill.ts`). */
  skill: number;
  dials: AiDials;
}

// THE CAST IS DATA (AI_MANAGERS.md §4.6): one file per character in
// `packages/bots/data`, read by `@fsp/bots`. This is the part of each a
// seat stores and a draft reads — who it is, how well it plays, its dials;
// its team's name and its lines stay in the file. It spans the styles
// (B-291): from Sunday Sam, who changes only what he must and never offers a
// trade, to Chaos Gremlin, who works the wire and the trade screen every day.
// The first eight are in the order the suite's goldens drafted with, and a
// new character joins at the end.
export const AI_PRESETS: readonly AiManager[] = CAST.map((c) => ({
  presetId: c.id,
  name: c.name,
  voice: c.voice,
  skill: BAND_SKILL[c.skill],
  dials: { ...c.dials },
}));

/**
 * Presets in a stable order by seat: the simulator's and the season suite's
 * cast, whose seasons are goldens. A real league's open seats take `castFor`.
 */
export function aiForSeat(index: number): AiManager {
  return AI_PRESETS[index % AI_PRESETS.length]!;
}

/** FNV-1a, then mulberry32: a stable stream of numbers in [0, 1) per string. */
function streamOf(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  let a = h >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * The bots for `count` open seats of a league, in seat order (B-291).
 *
 * A SHUFFLE, NOT A ROTATION. `aiForSeat` put Uncle Rico in seat 1 of every
 * league; this shuffles the cast by the league's own id, so leagues differ and
 * a retry of the same league gets the same cast.
 *
 * NO REPEATS UNTIL EVERY PRESET HAS A SEAT, counting the bots the league
 * already has (`seated`, their preset ids): each seat takes the least-used
 * preset, the shuffle breaking ties. The cast spans the styles, so a league's
 * bots are a spread of them and never eight of one. A repeat is named apart by
 * the caller (`uniqueBotName`).
 */
export function castFor(leagueId: string, seated: readonly string[], count: number): AiManager[] {
  const next = streamOf(`cast|${leagueId}`);
  const order = [...AI_PRESETS];
  for (let i = order.length - 1; i > 0; i -= 1) {
    const j = Math.floor(next() * (i + 1));
    [order[i], order[j]] = [order[j]!, order[i]!];
  }
  const uses = new Map(order.map((p) => [p.presetId, 0]));
  for (const id of seated) if (uses.has(id)) uses.set(id, uses.get(id)! + 1);
  const out: AiManager[] = [];
  for (let n = 0; n < count; n += 1) {
    let pick = order[0]!;
    for (const p of order) if (uses.get(p.presetId)! < uses.get(pick.presetId)!) pick = p;
    uses.set(pick.presetId, uses.get(pick.presetId)! + 1);
    out.push(pick);
  }
  return out;
}

/**
 * Which preset a stored bot is: its row's own `presetId`, else the preset its
 * name starts with ("Uncle Rico II"), else null. Rows written before the
 * dials were stored whole carry `{}`.
 */
export function presetIdOf(name: string, stored: unknown): string | null {
  if (typeof stored === 'object' && stored !== null && 'presetId' in stored) {
    const id = (stored as { presetId: unknown }).presetId;
    if (typeof id === 'string' && AI_PRESETS.some((p) => p.presetId === id)) return id;
  }
  return AI_PRESETS.find((p) => name === p.name || name.startsWith(`${p.name} `))?.presetId ?? null;
}

/**
 * How many of the top available a manager actually considers. The three
 * levels the cast had before the bands keep their numbers (`byBand`).
 */
function window(skill: number): number {
  return Math.round(byBand(skill, { along: 18, casual: 12, solid: 6, sharp: 3, ruthless: 2 }));
}

/**
 * How many more places down the list a bot with a style looks than one
 * without: far enough for its strongest taste to reach a player.
 */
const STYLE_REACH = 34;
/** A bot that tilts, when the player it wanted went just before it: louder noise, stronger tastes. */
const TILT_NOISE = 2.5;
const TILT_TASTE = 1.5;
/** A nervous bot with a long wait to its next pick weighs its missing starters this much more. */
const PANIC_NEED = 1.8;
/** Where a late position's best is weighed in the rounds a bot takes them sooner. */
const LATE_PLACE = 6;

/**
 * The AI's pick: the best available by rankings, blurred by skill and
 * risk, pulled toward a missing starter when one is missing, and never a
 * kicker or defense before the last rounds — and, in a league that names its
 * sport, bent by the bot's own draft style (`style.ts`, AI_MANAGERS.md §4.7).
 */
export function pickAi(state: DraftState, teamId: string, ctx: DraftContext): string | null {
  const ai = state.ai[teamId];
  if (!ai) return null;
  return choose(state, teamId, ctx, ai, draftStyleOf(ai, ctx, state.id), true);
}

function choose(
  state: DraftState,
  teamId: string,
  ctx: DraftContext,
  ai: AiManager,
  style: DraftStyle | null,
  mayTilt: boolean,
): string | null {
  // THE PICKS THIS TEAM HAS LEFT TO MAKE, as `bestAvailable` counts them: not
  // the rounds left, which count a keeper's round (whose pick is his already)
  // and a pick traded away. A bot whose last two rounds were keepers counted
  // them, saved its kicker and defense for picks it did not have, and went
  // into the season with neither (the bot league's chain, 2026-10-27).
  const overall = state.picks.length + 1;
  const roster = rosterOf(state, teamId);
  // AND A PICK TRADED, before the draft or in the room (`picksLeft`): a team
  // that traded its last round saved its kicker for it (the review of
  // PLAN-2026-10-27-the-next-season chunk 5).
  const roundsLeft = picksLeft(state, teamId);
  const need = needs(state, teamId, ctx);
  const lateNeeded = ctx.latePositions.filter((p) => need[p]).length;
  const saveLate = roundsLeft > lateNeeded;
  const countOf = (pos: string) => roster.filter((id) => ctx.positionOf(id) === pos).length;
  // A STYLE THAT TAKES THE LATE POSITIONS SOONER lets them in earlier; the
  // floor below keeps its own rule.
  const savesLate = roundsLeft > lateNeeded + (style?.lateEarly ?? 0);
  const look =
    style === null
      ? null
      : styleLook(state, teamId, ctx, style, { roster, auction: false, risk: ai.dials.risk });

  // A HOLE IN THE STARTING LINEUP IS NOT A MATTER OF TASTE. Below, a missing
  // starter is worth a bonus of 0.6 to a casual manager while the noise term
  // reaches ±4.5 — so once the rounds run out, personality was still deciding
  // whether to take the defense this team has none of. The season test found
  // three of twelve teams finishing with two kickers and no defense, or two
  // defenses and no kicker: rosters that pass `rosterProblem` (the league
  // allows empty slots) and cannot field a lineup.
  //
  // `bestAvailable` already draws this line and `autopick` has always had it,
  // so once there are no more rounds than there are missing starters, this
  // defers to the same rule rather than inventing a second one. That is the bar
  // every platform meets and the one AI_MANAGERS.md sets: the complaint about
  // bots is never that they play badly, it is that they are useless, and a bot
  // that cannot put eleven players on the field is useless.
  //
  // It costs personality only in the last rounds, and only when something is
  // actually missing. A team whose starters are filled still drafts for fun.
  const needCount = Object.values(need).reduce((a, b) => a + b, 0);
  if (roundsLeft <= needCount || !saveLate) {
    const forced = bestAvailable(state, teamId, ctx);
    if (forced !== null && need[ctx.positionOf(forced)]) {
      // THE SAME POSITION, from a club it will draft from where there is one:
      // a grudge gives way to an empty slot, never to the next kicker down.
      if (look === null || !styleRefuses(style!, look, forced, ctx)) return forced;
      const pos = ctx.positionOf(forced);
      const other = ctx.rankings.find(
        (id) =>
          ctx.positionOf(id) === pos && !isTaken(state, id) && !styleRefuses(style!, look, id, ctx),
      );
      return other ?? forced;
    }
  }

  const candidates = ctx.rankings
    .filter((id) => !isTaken(state, id))
    .filter((id) => {
      const pos = ctx.positionOf(id);
      const cap = ctx.maxPerPosition[pos];
      if (cap !== undefined && countOf(pos) >= cap) return false;
      // THE LEAGUE'S OWN CAP counts what it carried (§2.4): a fourth
      // quarterback on a roster that allows three is one to let go.
      if (atLeagueCap(state, teamId, pos, ctx)) return false;
      if (savesLate && ctx.latePositions.includes(pos)) return false;
      // A GRUDGE, A LIMIT OR A DISLIKE: not now, whatever his place.
      if (look !== null && styleRefuses(style!, look, id, ctx)) return false;
      return true;
    })
    .slice(0, window(ai.skill) + 6 + (style === null ? 0 : STYLE_REACH));
  if (candidates.length === 0) return bestAvailable(state, teamId, ctx);
  // Each candidate's place: where the list has him.
  const weighed = candidates.map((id, i) => ({ id, place: i }));
  // A LATE POSITION IT TAKES SOONER than the habit is far down any list, so in
  // the rounds it lets one in, the best it still needs is weighed as though he
  // were near the top (`lateEarly`).
  if (look !== null && saveLate && !savesLate) {
    for (const pos of ctx.latePositions) {
      if (!need[pos] || candidates.some((id) => ctx.positionOf(id) === pos)) continue;
      const late = ctx.rankings.find(
        (id) =>
          ctx.positionOf(id) === pos && !isTaken(state, id) && !styleRefuses(style!, look, id, ctx),
      );
      if (late !== undefined)
        weighed.push({ id: late, place: Math.min(LATE_PLACE, weighed.length) });
    }
  }

  // TILT: the player it would have taken went just before it, and the next
  // pick is rasher for it.
  const tilted = style !== null && style.tilt && mayTilt && sniped(state, teamId, ctx, ai, style);

  // Score: rank order, a need bonus, and risk-scaled noise. Casual managers
  // are noisier and care less about needs; sharp ones barely deviate. A style
  // moves each player by its places (`style.ts`).
  const noise =
    byBand(ai.skill, { along: 4, casual: 3, solid: 1.5, sharp: 0.6, ruthless: 0.3 }) *
    (0.5 + ai.dials.risk) *
    (tilted ? TILT_NOISE : 1);
  const needWeight =
    byBand(ai.skill, {
      along: 0.3,
      casual: 0.6,
      solid: 1.5,
      sharp: 2.5,
      ruthless: 3,
    }) * (look?.panicking ? PANIC_NEED : 1);
  let best: string | null = null;
  let bestScore = Infinity;
  weighed.forEach(({ id, place }) => {
    const pos = ctx.positionOf(id);
    const r = ctx.random(`${state.id}:${teamId}:${overall}:${id}`);
    const pull =
      look === null
        ? 0
        : stylePull(style!, look, id, ctx) * (tilted ? TILT_TASTE : 1) +
          roomPull(style!, look, id, ctx);
    const score = place - (need[pos] ? needWeight : 0) + (r - 0.5) * 2 * noise - pull;
    if (score < bestScore) {
      bestScore = score;
      best = id;
    }
  });
  return best;
}

/**
 * Whether the pick just before this one took the player this bot would have
 * taken in its place: the moment a person sighs and grabs whoever is next.
 */
/**
 * Whether the last pick took the player a bot seat would have taken next: its
 * choice with that pick undone, as its tilt reads it (`sniped`). The draft
 * room's face for the seat on the clock winces at it (B-529). False for a
 * person's seat, whose list is theirs alone, and for a bot that just picked.
 */
export function wantedTaken(state: DraftState, teamId: string, ctx: DraftContext): boolean {
  const ai = state.ai[teamId];
  if (!ai) return false;
  const last = state.picks[state.picks.length - 1];
  if (!last || last.teamId === teamId) return false;
  const before: DraftState = { ...state, picks: state.picks.slice(0, -1) };
  return choose(before, teamId, ctx, ai, draftStyleOf(ai, ctx, state.id), false) === last.playerId;
}

function sniped(
  state: DraftState,
  teamId: string,
  ctx: DraftContext,
  ai: AiManager,
  style: DraftStyle,
): boolean {
  const last = state.picks[state.picks.length - 1];
  if (!last || last.teamId === teamId) return false;
  const before: DraftState = { ...state, picks: state.picks.slice(0, -1) };
  return choose(before, teamId, ctx, ai, style, false) === last.playerId;
}
